'use client';

import type { AnchorHTMLAttributes, MouseEvent, ReactElement } from 'react';

import { useLinkLocation, useLinkToolName } from '@/components/ui/link-location-context';
import { MARKDOWN_LINK_CLASS, MarkdownLink } from '@/components/ui/markdown-link';
import { useAnalytics } from '@/hooks/use-analytics';
import { cn } from '@/lib/utils';
import { isDeviceHandlerLink } from '@/lib/utils/markdown-url';

// The OS handles the href, so there is no in-app navigation to report.
const DEVICE_HANDLER_MODE = 'device-handler';

// react-markdown injects its hast `node` as a prop on custom components; React then warns when
// that lands on a DOM element. Strip it before forwarding.
type MarkdownAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown };

/**
 * Anchor for every markdown-rendered link, in message bodies and tool-UI footers alike.
 *
 * `tel:`/`mailto:`/`sms:` reach a native anchor so the device handler (dialer, mail client) fires;
 * everything else — web, relative, hash, query — routes through MarkdownLink for baseUrl
 * resolution, iframe navigation, and analytics. Both paths carry the same link styling, and both
 * report a link click.
 */
export function MarkdownAnchor({
  href,
  className,
  node: _node,
  onClick,
  ...rest
}: MarkdownAnchorProps): ReactElement {
  const { trackLinkClick } = useAnalytics();
  const linkLocation = useLinkLocation();
  const toolName = useLinkToolName();

  function handleDeviceClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    // Never preventDefault: the default action *is* the dialer. The handler opens alongside a page
    // that stays loaded, so an ordinary capture has time to flush — no beacon needed.
    if (event.defaultPrevented || !href) return;
    trackLinkClick({
      url: href,
      linkText: event.currentTarget.textContent ?? undefined,
      navigationMode: DEVICE_HANDLER_MODE,
      source: linkLocation ?? 'text',
      ...(toolName && { toolName }),
    });
  }

  if (isDeviceHandlerLink(href))
    return (
      <a
        href={href}
        className={cn(MARKDOWN_LINK_CLASS, className)}
        onClick={handleDeviceClick}
        {...rest}
      />
    );
  return <MarkdownLink href={href} className={className} onClick={onClick} {...rest} />;
}
